import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, Loader2, Droplets } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { API_BASE } from '../../utils/api-base';

interface TagBinding {
  plcId: string;
  tagName: string;
  plcName?: string;
}

interface EquipmentItem {
  id: string;
  label: string;
  binding: TagBinding | null;
}

interface PlantKpiConfig {
  targetProduction: number;
  targetUnit: string;
  bindings: Record<string, TagBinding>;
  pumps: EquipmentItem[];
  filters: EquipmentItem[];
}

interface AvailableTag {
  plcId: string;
  plcName: string;
  tagName: string;
  unit?: string;
}

const DEFAULT_CONFIG: PlantKpiConfig = {
  targetProduction: 0,
  targetUnit: 'L',
  bindings: {},
  pumps: [],
  filters: [],
};

// Single-tag KPIs bound here, in display order. Energy Consumption is deliberately
// not included — that's covered by the dedicated Energy Meters screen instead.
const BINDING_FIELDS: { key: string; label: string; hint: string }[] = [
  { key: 'plantRunningStatus', label: 'Plant Running Status', hint: 'A status/run tag (non-zero = running)' },
  { key: 'productionToday',    label: 'Production Today',      hint: 'A totalizer tag for today’s production' },
  { key: 'totalWaterFiltered', label: 'Total Water Filtered',  hint: 'A totalizer tag for filtered volume' },
  { key: 'flowRate',           label: 'Flow Rate',             hint: 'An instantaneous flow-rate tag' },
  { key: 'filterRunningHours', label: 'Filter Running Hours',  hint: 'A running-hours totalizer tag' },
  { key: 'backwashFlow',       label: 'Backwash Water Flow',   hint: 'Used for the Treated Backwash Water % KPI' },
  { key: 'treatedFlow',        label: 'Treated Water Flow',    hint: 'Used for the Treated Backwash Water % KPI' },
];

const genId = () => `eq_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const bindingKey = (b: TagBinding | null | undefined) => (b ? `${b.plcId}::${b.tagName}` : null);

export function PlantKPIConfigTab() {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [config, setConfig] = useState<PlantKpiConfig>(DEFAULT_CONFIG);
  const [availableTags, setAvailableTags] = useState<AvailableTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const getSessionToken = () => sessionStorage.get()?.token || null;

  const loadClients = async () => {
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const data = await adminApi.getClients(token);
      setClients(data);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  useEffect(() => { loadClients(); }, []);

  const loadForClient = useCallback(async (clientId: string) => {
    const token = getSessionToken();
    if (!token) return;
    setLoading(true);
    try {
      const [cfgRes, plcsResponse] = await Promise.all([
        adminApi.getPlantKpiConfig(token, clientId),
        fetch(`${API_BASE}/api/clients/${clientId}/plcs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const cfg: PlantKpiConfig = { ...DEFAULT_CONFIG, ...(cfgRes?.config || {}) };
      setConfig(cfg);

      const plcsPayload = await plcsResponse.json();
      const plcs = Array.isArray(plcsPayload) ? plcsPayload : plcsPayload?.plcs || [];
      const tags: AvailableTag[] = [];
      for (const plc of plcs) {
        if (plc.connectionType === 'energy-meter') continue;
        for (const tag of plc.tags || []) {
          tags.push({ plcId: plc.id, plcName: plc.name, tagName: tag.name, unit: tag.unit });
        }
      }
      setAvailableTags(tags);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load Plant KPI configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClient) loadForClient(selectedClient);
  }, [selectedClient, loadForClient]);

  const updateBinding = (key: string, tag: AvailableTag | null) => {
    setConfig(prev => {
      const bindings = { ...prev.bindings };
      if (tag) bindings[key] = { plcId: tag.plcId, tagName: tag.tagName, plcName: tag.plcName };
      else delete bindings[key];
      return { ...prev, bindings };
    });
  };

  const updateEquipmentList = (listKey: 'pumps' | 'filters', next: EquipmentItem[]) => {
    setConfig(prev => ({ ...prev, [listKey]: next }));
  };

  const addEquipment = (listKey: 'pumps' | 'filters') => {
    const label = listKey === 'pumps' ? `Pump ${config.pumps.length + 1}` : `Filter ${config.filters.length + 1}`;
    updateEquipmentList(listKey, [...config[listKey], { id: genId(), label, binding: null }]);
  };

  const saveConfig = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const res = await adminApi.savePlantKpiConfig(token, selectedClient, config);
      const cfg: PlantKpiConfig = { ...DEFAULT_CONFIG, ...(res?.config || config) };
      setConfig(cfg);
      toast.success('Plant KPI configuration saved');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Droplets className="w-6 h-6 text-cyan-500" /> Plant KPI Configuration
        </h2>
        <p className="text-sm text-muted-foreground">
          Bind PLC tags to each Plant KPI Dashboard metric for a client. Clients only see the results —
          they can't change these bindings from their own portal.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select Client</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {clients.map((client) => (
              <Button
                key={client.id}
                variant={selectedClient === client.id ? 'default' : 'outline'}
                onClick={() => setSelectedClient(client.id)}
              >
                {client.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedClient && (
        loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading configuration…
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bind KPIs to PLC Tags</CardTitle>
              <CardDescription>
                Pick which tag feeds each KPI. Values update live once bound; anything left unbound just
                shows as "—" on the client's dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {BINDING_FIELDS.map(field => {
                  const current = config.bindings[field.key];
                  const currentKey = bindingKey(current) || '';
                  return (
                    <div key={field.key}>
                      <label className="text-xs font-semibold text-muted-foreground block mb-1">{field.label}</label>
                      <select
                        className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={currentKey}
                        onChange={e => {
                          const [plcId, tagName] = e.target.value.split('::');
                          const tag = availableTags.find(t => t.plcId === plcId && t.tagName === tagName) || null;
                          updateBinding(field.key, tag);
                        }}
                      >
                        <option value="">— Not bound —</option>
                        {availableTags.map(t => (
                          <option key={`${t.plcId}::${t.tagName}`} value={`${t.plcId}::${t.tagName}`}>
                            {t.plcName} / {t.tagName}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-muted-foreground mt-1">{field.hint}</p>
                    </div>
                  );
                })}
              </div>

              <div className="border-t pt-4 grid grid-cols-2 gap-4 max-w-sm">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Daily Production Target</label>
                  <Input
                    type="number" min={0}
                    value={config.targetProduction}
                    onChange={e => setConfig(prev => ({ ...prev, targetProduction: Math.max(0, Number(e.target.value) || 0) }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Unit</label>
                  <Input
                    value={config.targetUnit}
                    placeholder="L / m3 / KL"
                    onChange={e => setConfig(prev => ({ ...prev, targetUnit: e.target.value }))}
                  />
                </div>
              </div>

              {(['pumps', 'filters'] as const).map(listKey => (
                <div key={listKey} className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {listKey === 'pumps' ? 'Pumps' : 'Filters'}
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => addEquipment(listKey)}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add {listKey === 'pumps' ? 'Pump' : 'Filter'}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {config[listKey].map((item, idx) => {
                      const currentKey = bindingKey(item.binding) || '';
                      return (
                        <div key={item.id} className="flex items-center gap-2">
                          <Input
                            className="w-32"
                            value={item.label}
                            onChange={e => {
                              const next = [...config[listKey]];
                              next[idx] = { ...item, label: e.target.value };
                              updateEquipmentList(listKey, next);
                            }}
                          />
                          <select
                            className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                            value={currentKey}
                            onChange={e => {
                              const [plcId, tagName] = e.target.value.split('::');
                              const tag = availableTags.find(t => t.plcId === plcId && t.tagName === tagName);
                              const next = [...config[listKey]];
                              next[idx] = { ...item, binding: tag ? { plcId: tag.plcId, tagName: tag.tagName, plcName: tag.plcName } : null };
                              updateEquipmentList(listKey, next);
                            }}
                          >
                            <option value="">— Not bound —</option>
                            {availableTags.map(t => (
                              <option key={`${t.plcId}::${t.tagName}`} value={`${t.plcId}::${t.tagName}`}>
                                {t.plcName} / {t.tagName}
                              </option>
                            ))}
                          </select>
                          <Button variant="ghost" size="sm" onClick={() => updateEquipmentList(listKey, config[listKey].filter((_, i) => i !== idx))}>
                            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                          </Button>
                        </div>
                      );
                    })}
                    {config[listKey].length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No {listKey} added yet.</p>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button onClick={saveConfig} disabled={saving}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                  {saving ? 'Saving…' : 'Save Configuration'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
